import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { ManagerRecord, UserRecord } from '@libs/shared/api';
import { ServiceWorkerUpdateService } from '@libs/shared/services/service-worker-update.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AuthService } from '@sprint-backoffice/app/core/auth/auth.service';
import { ChannelIoService } from '@sprint-backoffice/app/core/script/channel-io/channel-io.service';
import { SseService } from '@sprint-backoffice/app/core/sse/sse.service';
import { injectCoreFeature } from '@sprint-backoffice/app/core/state/core.store';
import { environment } from '@sprint-backoffice/environments/environment';

@UntilDestroy()
@Component({
  selector: 'app-root',
  standalone: true,
  template: ` <router-outlet /> `,
  styles: [
    `
      :host {
        display: flex;
        flex: 1 1 auto;
        width: 100%;
        height: 100%;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet],
})
export class AppComponent implements OnInit, OnDestroy {
  readonly core = injectCoreFeature();
  readonly auth = inject(AuthService);
  readonly router = inject(Router);
  readonly channelIo = inject(ChannelIoService);
  readonly sse = inject(SseService);
  readonly serviceWorkerUpdateService = inject(ServiceWorkerUpdateService);

  isLoaded = false;
  isScriptLoaded = false;
  isSseOpened = false;

  /**
   * Constructor
   */
  constructor() {
    document.title = environment.title;

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        if (this.auth.isAuthenticated$$()) {
          if (!this.auth.hasValidAccessToken()) {
            this.auth.refreshToken();
          }
        }
      }
    });

    effect(() => {
      this.openChannelIo(
        this.core.currentUser$$(),
        this.core.currentManager$$()
      );
    });
  }

  ngOnInit(): void {
    this.addS3PreconnectToHtmlHead();
    this.auth.isAuthenticated$.subscribe(isAuthenticated => {
      if (isAuthenticated && !this.isLoaded) {
        this.bootstrap();
      }
      if (!isAuthenticated) {
        this.shutdown();
      }
    });
  }

  ngOnDestroy(): void {
    this.closeSse();
    this.closeChannelIo();
  }

  private bootstrap(): void {
    this.openSse();
    this.core.fetchAppStatus();

    this.isLoaded = true;
  }

  private shutdown(): void {
    this.closeSse();
    this.closeChannelIo();
    this.isLoaded = false;
    this.navigateToLoginPage();
  }

  private openSse(): void {
    if (!this.isSseOpened) {
      this.sse.buildConnection();
      this.isSseOpened = true;
    }
  }

  private addS3PreconnectToHtmlHead() {
    const link: HTMLLinkElement = document.createElement('link');
    link.rel = 'preconnect';
    link.href = environment.s3Path;
    document.head.appendChild(link);
  }

  private openChannelIo(user: UserRecord, manager: ManagerRecord): void {
    if (!this.isScriptLoaded && user && manager) {
      this.channelIo.loadScript();
      this.channelIo.boot(user, manager);
      this.isScriptLoaded = true;
    }
  }

  private closeSse(): void {
    if (this.isSseOpened) {
      this.sse.closeConnection();
    }
  }

  private closeChannelIo(): void {
    if (this.isScriptLoaded) {
      this.channelIo.shutdown();
    }
  }

  private navigateToLoginPage() {
    this.router.navigateByUrl('/home').then();
  }
}
